import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Copyable,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  type DataTableToolbarProps,
  DateUI,
  type FacetedFilterOption,
  FacetedFilterStandalone,
  type GetOptionsByValueBase,
  Icon,
  LabelPill,
  type OptionsByValue,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError, neverFunc, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { compareAsc, compareDesc } from "date-fns";
import { X } from "lucide-react";
import { DateTime } from "luxon";
import { Suspense, useMemo, useState } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { RiAddLine } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { MANUAL_FX_PROVIDER_LABELS } from "~/components/ManualFXTrades/manualFXProvider";
import { NewManualFXTradeModal } from "~/components/ManualFXTrades/NewManualFXTradeModal";
import { graphql } from "~/gql/client";
import {
  fXTradeStatusValues,
  fXTradeTypeValues,
} from "~/gql/client/enumValues";
import type { FxTradeList_FxTradesFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/fx-trades")({
  head: () => opsHead("FX Trades"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data, error } = useSuspenseQuery(
    graphql(`
      query FXTradesIndexPage {
        ...FXTradeList_FXTrades
        ...NewManualFXTradeModal_Organisations
      }

      fragment FXTradeList_FXTrades on Query {
        fxTrades {
          id
          amount {
            amount
            currency
          }
          sellAmount {
            amount
            currency
          }
          buyAmount {
            amount
            currency
          }
          externalId
          operation
          businessEntity {
            id
            name
            organisation {
              id
              name
              squareLogoUrl
            }
          }
          processAfterDateTime
          status
          valueDate
          tradeType
          ... on ManualFXTrade {
            manualFxProvider
          }
        }
      }
    `),
  );
  if (error) {
    throw error;
  }
  if (!data || data.fxTrades === null || data.fxTrades === undefined) {
    throw new NotFoundError();
  }

  const [newManualModalOpen, setNewManualModalOpen] = useState<boolean>(false);
  const [triggerCron, { loading: isSyncing }] = useMutation(
    graphql(`
      mutation AdminFXTradesTriggerCron($input: TriggerCronInput!) {
        triggerCron(input: $input) {
          jobId
          taskId
        }
      }
    `),
  );

  const trades = data.fxTrades
    .slice()
    .sort((a, b) =>
      compareDesc(a.processAfterDateTime, b.processAfterDateTime),
    );

  return (
    <AdminPageContainer testId="admin-fx-trades">
      <AdminPageHeader
        title="FX Trades"
        buttons={
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={isSyncing}
              onClick={() => {
                void triggerCron({
                  variables: {
                    input: {
                      taskId: "pollEburyClientsCronTask",
                    },
                  },
                })
                  .then(() => {
                    toast({
                      title: "Ebury sync triggered",
                      kind: "success",
                      autoHide: true,
                    });
                  })
                  .catch((error) => {
                    toast({
                      kind: "error",
                      autoHide: false,
                      title: "Failed to trigger Ebury sync",
                      description: String(error),
                    });
                  });
              }}
            >
              Sync with Ebury
            </Button>
            <Link to="/fx-trades/new">
              <Button leftIcon={<RiAddLine />}>New FX trade</Button>
            </Link>
            <Button
              variant="outline"
              leftIcon={<RiAddLine />}
              onClick={() => setNewManualModalOpen(true)}
            >
              New manual FX trade
            </Button>
          </div>
        }
      />
      <DataTable
        data={trades}
        columns={columns}
        linkTo={(row) => `/fx-trades/${row.original.id}`}
        className="overflow-hidden border"
        toolbarComponent={DataTableToolbar}
        initialColumnVisibility={{ tradeType: false }}
      />
      <NewManualFXTradeModal
        open={newManualModalOpen}
        onOpenChange={setNewManualModalOpen}
        organisationOptions={data.organisations}
      />
    </AdminPageContainer>
  );
}

type FXTradeRow = NonNullable<FxTradeList_FxTradesFragment["fxTrades"]>[number];

const fxProviderFilterOptions: {
  label: string;
  value: FXTradeRow["__typename"];
}[] = [
  { label: "Ebury", value: "EburyFXTrade" },
  { label: "Visa", value: "CurrencycloudFXTrade" },
  { label: "Manual", value: "ManualFXTrade" },
];

function DataTableToolbar({ table }: DataTableToolbarProps<FXTradeRow>) {
  const isFiltered =
    table.getPreFilteredRowModel().rows.length >
    table.getFilteredRowModel().rows.length;

  const preFilteredRows = table.getPreFilteredRowModel().rows;

  const organisationIdOptionsByValue = useMemo<OptionsByValue>(
    () => getOrganisationIdOptionsByValue({ preFilteredRows }),
    [preFilteredRows],
  );

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {table.getColumn("status") && (
          <FacetedFilterStandalone
            column={table.getColumn("status")}
            title="Status"
            options={fXTradeStatusValues.map((value) => ({
              label: prettyPrintEnum(value),
              value,
            }))}
          />
        )}
        {table.getColumn("tradeType") && (
          <FacetedFilterStandalone
            column={table.getColumn("tradeType")}
            title="Trade type"
            options={fXTradeTypeValues.map((value) => ({
              label: prettyPrintEnum(value),
              value,
            }))}
          />
        )}
        {table.getColumn("organisationId") && (
          <FacetedFilterStandalone
            column={table.getColumn("organisationId")}
            title="Organisation"
            options={Object.values(organisationIdOptionsByValue).sort(
              (a, b) =>
                a.label?.toString().localeCompare(b.label?.toString() ?? "") ??
                0,
            )}
          />
        )}
        {table.getColumn("externalId") && (
          <FacetedFilterStandalone
            column={table.getColumn("externalId")}
            title="Provider"
            options={fxProviderFilterOptions}
          />
        )}
        {isFiltered && (
          <Button
            rightIcon={<X />}
            variant="ghost"
            size="sm"
            onClick={() => table.resetColumnFilters()}
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}

type GetOptionsByValue = GetOptionsByValueBase<FXTradeRow>;

const getOrganisationIdOptionsByValue: GetOptionsByValue = ({
  preFilteredRows,
}) =>
  preFilteredRows.reduce(
    (acc, row) => {
      const organisation = row.original.businessEntity?.organisation;
      if (!organisation) {
        return acc;
      }

      if (organisation && !acc[organisation.id]) {
        acc[organisation.id] = {
          label: organisation.name,
          value: organisation.id,
        };
      }
      return acc;
    },
    {} as Record<string, FacetedFilterOption>,
  );

const columns: ColumnDef<
  NonNullable<FxTradeList_FxTradesFragment["fxTrades"]>[number]
>[] = [
  {
    id: "organisationId",
    accessorFn: (row) => row.businessEntity?.organisation?.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client" />
    ),
    cell: ({ row }) => (
      <>
        {row.original.businessEntity?.organisation ? (
          <div className="flex items-center gap-2">
            <Icon
              size="8"
              imageUrl={
                row.original.businessEntity.organisation.squareLogoUrl ??
                undefined
              }
              fallbackIcon={<PiBuildingOffice />}
            />
            <div className="flex flex-col items-start">
              <Txt variant="name">
                {row.original.businessEntity.organisation.name}
              </Txt>
              <Txt variant="caption">{row.original.businessEntity.name}</Txt>
            </div>
          </div>
        ) : (
          <div>No organisation.</div>
        )}
      </>
    ),
    sortingFn: (a, b) => {
      const aVal = a.original.businessEntity?.organisation?.name ?? "";
      const bVal = b.original.businessEntity?.organisation?.name ?? "";
      return aVal.localeCompare(bVal);
    },
    filterFn: (row, _id, value: string[]) =>
      !!row.original.businessEntity?.organisation &&
      value.includes(row.original.businessEntity?.organisation.id),
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <StatusUI status={row.original.status} />,
    sortingFn: (a, b) => a.original.status.localeCompare(b.original.status),
    filterFn: (row, _id, value: string[]) =>
      value.includes(row.original.status),
  },
  {
    id: "tradeType",
    accessorFn: (row) => row.tradeType,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Trade type" />
    ),
    cell: () => null,
    enableHiding: true,
    filterFn: (row, _id, value: string[]) =>
      !!row.original.tradeType && value.includes(row.original.tradeType),
  },
  {
    id: "valeDate",
    accessorFn: (row) => row.processAfterDateTime,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Value date" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex gap-2">
          {row.original.tradeType && (
            <LabelPill
              variant={row.original.tradeType === "SPOT" ? "warning" : "error"}
              label={prettyPrintEnum(row.original.tradeType)}
            />
          )}
          <DateUI
            date={DateTime.fromISO(row.original.valueDate).toJSDate()}
            variant="DATE"
          />
        </div>
      );
    },
    sortingFn: (a, b) =>
      compareAsc(
        a.original.processAfterDateTime,
        b.original.processAfterDateTime,
      ),
  },
  {
    id: "processAfterDateTime",
    accessorFn: (row) => row.processAfterDateTime,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Maturity Date" />
    ),
    cell: ({ row }) => {
      return (
        <DateUI date={row.original.processAfterDateTime} variant="DATETIME" />
      );
    },
    sortingFn: (a, b) =>
      compareAsc(
        a.original.processAfterDateTime,
        b.original.processAfterDateTime,
      ),
  },
  {
    id: "trade",
    accessorFn: (row) => row.operation,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => {
      switch (row.original.operation) {
        case "BUY":
          return (
            <div className="flex gap-2">
              Buy{" "}
              <CurrencyAmountUI
                amount={row.original.buyAmount.amount}
                currency={row.original.buyAmount.currency}
              />
            </div>
          );
        case "SELL":
          return (
            <div className="flex gap-2">
              Sell
              <CurrencyAmountUI
                amount={row.original.sellAmount.amount}
                currency={row.original.sellAmount.currency}
              />
            </div>
          );
        default:
          return neverFunc(row.original.operation);
      }
    },
    sortingFn: (a, b) =>
      a.original.amount.amount.comparedTo(b.original.amount.amount),
  },
  {
    id: "externalId",
    accessorFn: (row) => row.externalId,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="External ID" />
    ),
    cell: ({ row }) => {
      let fxProvider;
      switch (row.original.__typename) {
        case "EburyFXTrade":
          fxProvider = "Ebury";
          break;
        case "CurrencycloudFXTrade":
          fxProvider = "Visa";
          break;
        case "CorpayFXTrade":
          fxProvider = "Corpay";
          break;
        case "ManualFXTrade":
          fxProvider = row.original.manualFxProvider
            ? MANUAL_FX_PROVIDER_LABELS[row.original.manualFxProvider]
            : "Manual";
          break;
        default:
          neverFunc(row.original);
      }

      return (
        <div className="flex flex-col gap-1">
          <div>{fxProvider}</div>
          <Copyable
            tooltipSide="right"
            className="text-start font-mono text-xs"
          >
            {row.original.externalId ?? "None"}
          </Copyable>
        </div>
      );
    },
    filterFn: (row, _id, value: string[]) =>
      value.includes(row.original.__typename),
  },
];
